/**
 * Monta uma escaramuça a partir do conteúdo real do acervo.
 *
 * Nada aqui é inventado: as classes, as armas e o mapa saem do que o pipeline
 * converteu do Saga de Distortidus. As posições iniciais são encontradas no
 * próprio mapa, procurando casas livres nos cantos — até a Etapa 7, quando o
 * editor passa a declará-las.
 */

import {
  MAX_INVENTORY,
  buildContent,
  footprint,
  isConditionId,
  isConsumable,
  isWeaponSlot,
  normalizeSize,
  statsAtLevel,
  type Affliction,
  type AiDifficulty,
  type AiProfile,
  type ClassDef,
  type Content,
  type GameMap,
  type ItemDef,
  type ItemInstance,
  type MatchState,
  type Objective,
  type Stats,
  type Team,
  type TerrainDef,
  type TriangleRules,
  type Unit,
  type UnitSize,
  type Vec2,
  type WeaponDef,
} from '@oz/rules';
import { ASSETS_BASE } from '../assets/manifest.js';
import { mapArtFootprint } from '../render/map-art.js';
import type {
  MapChestData, MapData, MapObjectiveData, MapPlacementData, TilesetDef,
} from '../render/tilemap.js';
import { toItemDef, type AcervoItem } from './items.js';
import type { ChapterRecruit } from './story.js';

/**
 * O que a escaramuça precisa saber de um monstro do acervo.
 *
 * A ficha completa mora no editor de inimigos; aqui só entram os campos que
 * decidem quem aparece no tabuleiro e como ele se comporta.
 */
export interface AcervoEnemy {
  readonly id: number;
  readonly name: string;
  readonly battler: string;
  readonly aiProfile?: string | undefined;
  readonly mapSprite?: { sheet: string; index: number } | undefined;
  /**
   * Quantas casas a criatura ocupa no tabuleiro, quando o autor escolheu.
   *
   * Sem este campo a área sai medida do desenho — o que acerta o dragão e erra
   * o resto: um battler pequeno de uma criatura que DEVERIA ser enorme entra
   * numa casa só, e não há nada no painel que permita dizer o contrário. Aqui
   * a escolha do autor vence a régua.
   */
  readonly size?: { readonly w: number; readonly h: number } | undefined;
  /**
   * O que o golpe da criatura causa, venha de que arma vier — a peçonha do
   * gnomo, o hálito de fogo da salamandra. O pipeline tira das habilidades do
   * RPG Maker; o editor de inimigos ajusta.
   */
  readonly inflicts?: readonly Affliction[] | undefined;
}

/** Só as aflições que o motor conhece, com chance válida. */
export function cleanAfflictions(raw: readonly Affliction[] | undefined): Affliction[] | undefined {
  const out = (raw ?? []).filter(
    (a) => isConditionId(a.condition) && Number.isFinite(a.chance) && a.chance > 0,
  );
  return out.length > 0 ? out : undefined;
}

/**
 * O que a escaramuça precisa saber de um personagem do acervo.
 *
 * O esquadrão do jogador é uma lista fixa aqui no código — nomes, classes e
 * armas escolhidos à mão para uma batalha equilibrada —, mas a APARÊNCIA de
 * cada herói é dado do acervo, e é o painel de personagens que a edita. Sem
 * esta consulta as duas versões divergem em silêncio: foi assim que Dulpbuk
 * entrou em campo com o rosto de Clodz, o vizinho dele na folha de retratos,
 * enquanto o painel mostrava o rosto certo.
 */
export interface AcervoCharacter {
  readonly id: number;
  readonly name: string;
  readonly portrait?: { sheet: string; index: number } | undefined;
  readonly mapSprite?: { sheet: string; index: number } | undefined;
  readonly battler?: string | undefined;
}

export interface SkirmishContent {
  readonly content: Content;
  readonly classes: readonly ClassDef[];
  readonly weapons: readonly WeaponDef[];
  /**
   * Os itens do RPG como o acervo os traz — nome, ícone, descrição. O motor
   * recebe só os que servem em campo, em `content.items`.
   */
  readonly items?: readonly AcervoItem[] | undefined;
  readonly map: MapData;
  readonly tileset: TilesetDef;
  /**
   * Os monstros do acervo, para montar o lado da CPU num mapa sem anotação.
   *
   * Opcional porque o mapa anotado traz as próprias tropas e não precisa
   * deles — e porque um chamador que não os passe continua tendo partida, com
   * o esquadrão de reserva.
   */
  readonly enemies?: readonly AcervoEnemy[] | undefined;
  /**
   * Os personagens do acervo, para vestir o esquadrão do jogador.
   *
   * Opcional pela mesma razão dos monstros: o editor e os testes montam
   * escaramuças sem passar pelo acervo, e a aparência de reserva — a que está
   * escrita em `PLAYER_SQUAD` — continua valendo quando ele não vem.
   */
  readonly roster?: readonly AcervoCharacter[] | undefined;
}

interface RulesFile {
  readonly weaponTriangle: TriangleRules;
  readonly terrain: Record<string, TerrainDef>;
}

const json = async <T>(path: string): Promise<T> =>
  fetch(`${ASSETS_BASE}/${path}`).then((r) => {
    if (!r.ok) throw new Error(`${path}: HTTP ${r.status}`);
    return r.json() as Promise<T>;
  });

const fetchContentWithFallback = async <T>(apiEndpoint: string, staticPath: string): Promise<T> => {
  try {
    const res = await fetch(apiEndpoint);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) return data as T;
    }
  } catch {
    // API offline ou erro de rede: usa fallback local
  }
  return json<T>(staticPath);
};

const fetchMapWithFallback = async (mapId: number): Promise<MapData> => {
  // Um 404 é resposta, e não falha de rede: o servidor já olhou o banco E o
  // arquivo, e é ele também quem sabe que o mapa foi excluído do acervo.
  let gone = false;

  try {
    const res = await fetch(`/api/maps/${mapId}`);
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data.width === 'number' && typeof data.height === 'number') {
        return data as MapData;
      }
    }
    gone = res.status === 404;
  } catch {
    // API offline ou falha de rede: usa arquivo estático
  }

  /*
   * Cair no arquivo estático depois de um 404 ressuscitava o mapa que um
   * administrador tinha acabado de tirar do acervo: ele sumia do seletor e
   * continuava jogável por quem chegasse pelo id.
   */
  if (gone) throw new Error(`Mapa #${mapId} não está mais no acervo.`);

  return json<MapData>(`maps/${String(mapId).padStart(3, '0')}.json`);
};

/**
 * Monta a escaramuça.
 *
 * `draftMap` existe para o botão "Jogar este Mapa" do editor: o mapa em edição
 * pode ainda não estar no banco — ou o autor pode não ter permissão para
 * gravá-lo — e testar o traçado não deveria depender de persistir nada.
 */
export async function loadSkirmishContent(
  mapId: number,
  draftMap?: MapData,
): Promise<SkirmishContent> {
  const [classes, weapons, items, rules, tilesets, map, enemies, roster] = await Promise.all([
    fetchContentWithFallback<ClassDef[]>('/api/content/classes', 'content/classes.json'),
    fetchContentWithFallback<WeaponDef[]>('/api/content/weapons', 'content/weapons.json'),
    // Os itens do RPG que vão para a bolsa. Falhar aqui não impede a partida:
    // a bolsa perde os itens e continua com as armas.
    fetchContentWithFallback<AcervoItem[]>('/api/content/items', 'content/items.json')
      .catch(() => [] as AcervoItem[]),
    json<RulesFile>('content/rules.json'),
    json<TilesetDef[]>('content/tilesets.json'),
    draftMap ? Promise.resolve(draftMap) : fetchMapWithFallback(mapId),
    // O bestiário do acervo é quem povoa o lado da CPU. Sem ele a escaramuça
    // caía num punhado de aldeões de nome fixo — quatro humanos numa lista de
    // monstros, que era o que os mapas do acervo vinham mostrando.
    fetchContentWithFallback<AcervoEnemy[]>('/api/content/enemies', 'content/enemies.json')
      .catch(() => [] as AcervoEnemy[]),
    // O elenco do jogador. Falhar aqui não impede a partida: sem ele o
    // esquadrão entra com a aparência de reserva, que é a de sempre.
    fetchContentWithFallback<AcervoCharacter[]>('/api/content/characters', 'content/characters.json')
      .catch(() => [] as AcervoCharacter[]),
  ]);

  const tileset = tilesets.find((t) => t.id === map.tilesetId);
  if (!tileset) throw new Error(`tileset ${map.tilesetId} não existe no acervo`);

  return {
    content: buildContent({
      classes,
      weapons,
      items: items.map(toItemDef),
      terrain: rules.terrain,
      triangle: rules.weaponTriangle,
    }),
    classes,
    weapons,
    items,
    map,
    tileset,
    enemies,
    roster,
  };
}

/**
 * O objetivo do mapa no formato do motor.
 *
 * O documento vem do banco ou do editor, então nada garante que os campos que
 * cada tipo exige estejam lá: "sobreviver" sem `turns` e "tomar_posicao" sem
 * `at` produziriam uma partida que nunca termina. Quando falta o dado, cai no
 * objetivo clássico do Fire Emblem — derrotar o comandante.
 */
export function toObjective(raw: MapObjectiveData | undefined): Objective {
  switch (raw?.kind) {
    case 'derrotar_todos':
      return { kind: 'derrotar_todos' };
    case 'sobreviver':
      return typeof raw.turns === 'number' && raw.turns > 0
        ? { kind: 'sobreviver', turns: raw.turns }
        : { kind: 'derrotar_comandante' };
    case 'tomar_posicao':
      return raw.at ? { kind: 'tomar_posicao', at: { x: raw.at.x, y: raw.at.y } } : { kind: 'derrotar_comandante' };
    case 'derrotar_comandante':
    default:
      return { kind: 'derrotar_comandante' };
  }
}

/** O mapa no formato que o motor entende: só terreno, sem arte. */
export function toGameMap(map: MapData): GameMap {
  return {
    width: map.width,
    height: map.height,
    tiles: map.terrain,
    terrainTypes: map.terrainTypes,
    // Mapa sem teleporte nenhum não ganha o campo: o estado de partida é
    // gravado e comparado byte a byte, e uma lista vazia a mais mudaria todo
    // replay já existente sem mudar uma regra sequer.
    ...(map.teleports && map.teleports.length > 0 ? { teleports: map.teleports } : {}),
  };
}

/**
 * Os perfis de IA que o motor sabe jogar.
 *
 * O mapa vem do banco, e do banco vem texto: um perfil escrito errado — ou
 * gravado por uma versão anterior do editor — chegaria ao motor como um valor
 * que nenhum ramo trata, e a unidade ficaria parada a partida inteira sem que
 * nada acusasse o problema. Aqui ele volta a ser o comportamento padrão.
 */
const AI_PROFILES: ReadonlySet<string> = new Set([
  'agressivo', 'defensivo', 'guardiao', 'cacador', 'patrulha', 'chefe',
]);

export function toAiProfile(raw: string | undefined): AiProfile {
  return raw !== undefined && AI_PROFILES.has(raw) ? (raw as AiProfile) : 'agressivo';
}

const BLOCKED_TERRAIN = new Set(['muralha', 'agua', 'abismo']);

/**
 * O que basta de um mapa para decidir onde uma unidade cabe.
 *
 * As mesmas contas servem à partida e ao editor, e o mapa em edição não é um
 * `MapData` completo — pode não ter id, sombras nem arte. Pedir o mapa inteiro
 * só para contar casas obrigaria o editor a inventar campos que ele não tem.
 */
export type Ground = Pick<MapData, 'width' | 'height' | 'terrain' | 'terrainTypes'> &
  Partial<Pick<MapData, 'teleports'>>;

function isOpen(map: Ground, index: number): boolean {
  const name = map.terrainTypes[map.terrain[index] ?? 0];
  return name !== undefined && !BLOCKED_TERRAIN.has(name);
}

/** As saídas de teleporte de cada casa, por índice no vetor de terreno. */
function teleportEdges(map: Ground): ReadonlyMap<number, number[]> {
  const edges = new Map<number, number[]>();
  const link = (from: number, to: number): void => {
    const list = edges.get(from);
    if (list) list.push(to);
    else edges.set(from, [to]);
  };
  for (const gate of map.teleports ?? []) {
    const a = gate.a.y * map.width + gate.a.x;
    const b = gate.b.y * map.width + gate.b.x;
    if (a === b) continue;
    link(a, b);
    link(b, a);
  }
  return edges;
}

function walkFrom(map: Ground, start: number, edges: ReadonlyMap<number, number[]>): Map<number, number> {
  const steps = new Map<number, number>([[start, 0]]);
  const queue: number[] = [start];

  for (let head = 0; head < queue.length; head++) {
    const at = queue[head]!;
    const depth = steps.get(at)!;
    const x = at % map.width;
    const y = Math.floor(at / map.width);

    const around = [
      y > 0 ? at - map.width : -1,
      x > 0 ? at - 1 : -1,
      x < map.width - 1 ? at + 1 : -1,
      y < map.height - 1 ? at + map.width : -1,
      ...(edges.get(at) ?? []),
    ];

    for (const next of around) {
      if (next < 0 || steps.has(next) || !isOpen(map, next)) continue;
      steps.set(next, depth + 1);
      queue.push(next);
    }
  }

  return steps;
}

/**
 * A maior área contígua do mapa, contando teleporte como passagem.
 *
 * Metade dos mapas do acervo é um punhado de cômodos que, na origem, se
 * alcançavam por evento de porta. Nascer numa dessas ilhas é nascer preso: o
 * esquadrão anda em círculos e a partida nunca acontece. Os dois lados são
 * posicionados aqui dentro, e só aqui.
 */
export function battleground(map: Ground): ReadonlySet<number> {
  const edges = teleportEdges(map);
  const seen = new Set<number>();
  let best: Set<number> = new Set();

  for (let index = 0; index < map.width * map.height; index++) {
    if (seen.has(index) || !isOpen(map, index)) continue;
    const reached = new Set(walkFrom(map, index, edges).keys());
    for (const tile of reached) seen.add(tile);
    if (reached.size > best.size) best = reached;
  }

  return best;
}

/** A casa da área mais próxima do ponto desejado. */
function nearestInRegion(map: Ground, region: ReadonlySet<number>, wanted: Vec2): Vec2 | null {
  let best: Vec2 | null = null;
  let bestDistance = Infinity;

  for (const index of region) {
    const x = index % map.width;
    const y = Math.floor(index / map.width);
    const distance = Math.abs(x - wanted.x) + Math.abs(y - wanted.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = { x, y };
    }
  }

  return best;
}

/**
 * Onde plantar o outro esquadrão: perto do canto pretendido, longe do primeiro.
 *
 * A distância que conta é a de caminhada dentro da área — a linha reta
 * atravessa paredes e diria "longe" de uma casa que fica do outro lado de um
 * muro, a dois passos pela porta.
 */
function farthestInRegion(
  map: Ground,
  region: ReadonlySet<number>,
  from: Vec2,
  wanted: Vec2,
  minimum: number,
): Vec2 | null {
  const steps = walkFrom(map, from.y * map.width + from.x, teleportEdges(map));

  let candidate: Vec2 | null = null;
  let candidateDistance = Infinity;
  let fallback: Vec2 | null = null;
  let fallbackSteps = -1;

  for (const index of region) {
    const walk = steps.get(index);
    if (walk === undefined) continue;

    const x = index % map.width;
    const y = Math.floor(index / map.width);

    if (walk > fallbackSteps) {
      fallbackSteps = walk;
      fallback = { x, y };
    }

    if (walk < minimum) continue;
    const distance = Math.abs(x - wanted.x) + Math.abs(y - wanted.y);
    if (distance < candidateDistance) {
      candidateDistance = distance;
      candidate = { x, y };
    }
  }

  return candidate ?? fallback;
}

/**
 * Casas de ilha por unidade do esquadrão.
 *
 * Quatro recrutas numa alcova de seis casas cabem — e não jogam: cada um trava
 * o vizinho, a boca do portal que é a única saída acaba embaixo de alguém, e o
 * primeiro turno é quatro unidades sem para onde ir. Quatro casas por cabeça é
 * onde, nos mapas do acervo e nos importados, o esquadrão passa a se desdobrar
 * em vez de se entupir: baixá-lo para três traz de volta os cômodos de treze e
 * quinze casas com um exército inteiro dentro.
 */
const ELBOW_ROOM = 4;

/**
 * As ilhas do campo de batalha: o que se alcança A PÉ, sem passar por portal.
 *
 * O campo de batalha é a área contígua CONTANDO os teleportes — é o que
 * garante que os dois lados se encontrem. Mas um esquadrão inteiro plantado do
 * outro lado de um portal joga o primeiro turno espremido num cômodo, e é essa
 * diferença que esta função enxerga: dentro do campo, onde dá para andar sem
 * atravessar nada.
 */
function walkingIslands(map: Ground, region: ReadonlySet<number>): ReadonlySet<number>[] {
  const seen = new Set<number>();
  const islands: ReadonlySet<number>[] = [];

  for (const start of region) {
    if (seen.has(start)) continue;
    const island = new Set<number>([start]);
    const queue = [start];
    seen.add(start);

    for (let head = 0; head < queue.length; head++) {
      const at = queue[head]!;
      const x = at % map.width;
      const y = Math.floor(at / map.width);
      const around = [
        y > 0 ? at - map.width : -1,
        x > 0 ? at - 1 : -1,
        x < map.width - 1 ? at + 1 : -1,
        y < map.height - 1 ? at + map.width : -1,
      ];
      for (const next of around) {
        if (next < 0 || seen.has(next) || !region.has(next)) continue;
        seen.add(next);
        island.add(next);
        queue.push(next);
      }
    }

    islands.push(island);
  }

  return islands;
}

/**
 * O chão de um esquadrão: uma ilha só, e com espaço para manobrar.
 *
 * Antes a única exigência era "dentro do campo de batalha", e o campo conta os
 * portais: a âncora caía na casa mais próxima do canto pretendido, que num
 * mapa importado podia ser uma alcova de dez casas do outro lado de um
 * teleporte. Os quatro recrutas nasciam lá dentro, um trancando o outro, com a
 * boca do portal ocupada — e o turno abria sem jogada possível.
 *
 * A escolha agora é: entre as ilhas em que o esquadrão CABE, a mais próxima do
 * canto pretendido; e se nenhuma couber, a maior que houver. Espaço ganha do
 * canto, porque o canto é preferência e o espaço é a condição de jogar.
 */
function squadGround(
  map: Ground,
  region: ReadonlySet<number>,
  wanted: Vec2,
  count: number,
): ReadonlySet<number> {
  const islands = walkingIslands(map, region);
  if (islands.length <= 1) return region;

  const room = count * ELBOW_ROOM;
  const fits = islands.filter((island) => island.size >= room);
  if (fits.length === 0) {
    return islands.reduce((best, island) => (island.size > best.size ? island : best));
  }

  let best = fits[0]!;
  let bestDistance = Infinity;
  for (const island of fits) {
    const near = nearestInRegion(map, island, wanted);
    if (!near) continue;
    const distance = Math.abs(near.x - wanted.x) + Math.abs(near.y - wanted.y);
    if (distance < bestDistance || (distance === bestDistance && island.size > best.size)) {
      bestDistance = distance;
      best = island;
    }
  }
  return best;
}

/**
 * Casas livres perto de um canto, em ordem de proximidade.
 *
 * Espalhar as unidades a partir do canto — e não em fila — evita o bloco
 * compacto que morre para um único ataque em área e que, num corredor, trava
 * o próprio esquadrão.
 */
function deployment(
  map: Ground,
  corner: Vec2,
  count: number,
  occupied: readonly Vec2[] = [],
  region?: ReadonlySet<number>,
  wider?: ReadonlySet<number>,
): Vec2[] {
  const gates = teleportEdges(map);

  const openIn = (
    ground: ReadonlySet<number> | undefined,
    taken: ReadonlySet<string>,
  ): Vec2[] => {
    const open: { at: Vec2; distance: number; isPlain: boolean; isGate: boolean }[] = [];

    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        if (taken.has(`${x},${y}`)) continue;
        const index = y * map.width + x;
        if (ground && !ground.has(index)) continue;
        const name = map.terrainTypes[map.terrain[index] ?? 0];
        if (!name || name === 'muralha' || name === 'agua' || name === 'abismo') continue;
        open.push({
          at: { x, y },
          distance: Math.abs(x - corner.x) + Math.abs(y - corner.y),
          isPlain: name === 'planicie',
          // Boca de teleporte é passagem, não posto: começar a partida parado em
          // cima de uma é tapar a porta pela qual o esquadrão vai precisar sair.
          isGate: gates.has(index),
        });
      }
    }

    // Longe das bocas de teleporte, planície primeiro, depois distância da âncora
    open.sort(
      (a, b) =>
        (a.isGate ? 1 : 0) - (b.isGate ? 1 : 0) ||
        (b.isPlain ? 1 : 0) - (a.isPlain ? 1 : 0) ||
        a.distance - b.distance ||
        a.at.y - b.at.y ||
        a.at.x - b.at.x,
    );
    return open.map((o) => o.at);
  };

  const taken = new Set(occupied.map((p) => `${p.x},${p.y}`));
  const spots = openIn(region, taken).slice(0, count);

  // A ilha escolhida pode não ter casa livre para todo mundo — o outro
  // esquadrão pode ter chegado antes e ocupado o que havia. Quem sobrou vai
  // para o campo de batalha inteiro: ficar de fora do tabuleiro é pior do que
  // nascer longe do resto do esquadrão.
  if (spots.length < count && wider && wider !== region) {
    for (const spot of spots) taken.add(`${spot.x},${spot.y}`);
    spots.push(...openIn(wider, taken).slice(0, count - spots.length));
  }

  return spots;
}

interface Recruit {
  readonly name: string;
  readonly className: string;
  readonly weaponName: string;
  readonly portrait: { sheet: string; index: number };
  readonly sprite: { sheet: string; index: number };
  readonly battler: string;
  /**
   * A área declarada no painel, em casas. Vence a medida tirada do desenho.
   */
  readonly size?: { readonly w: number; readonly h: number } | undefined;
  /** Ver `UnitPresentation.battlerOnMap`. */
  readonly battlerOnMap?: boolean;
  /** Ver `UnitPresentation.battlerAsPortrait`. */
  readonly battlerAsPortrait?: boolean;
  readonly commander?: boolean;
  readonly aiProfile?: AiProfile;
  readonly level?: number;
  /**
   * De qual personagem do acervo esta unidade é.
   *
   * Presente só quando o esquadrão vem da campanha ou do online. É por ele que
   * o resultado da batalha volta para o registro certo no fim do capítulo — o
   * nome não serve, porque dois jogos importados podem trazer homônimos.
   */
  readonly characterId?: number;
  /** Os atributos já ganhos, quando a campanha os guardou. */
  readonly stats?: Readonly<Record<string, number>> | undefined;
  /** A bolsa como ela ficou no capítulo anterior, com os usos gastos. */
  readonly inventory?: readonly ItemInstance[] | undefined;
  /** A peçonha da criatura, quando ela vem do bestiário. Ver `AcervoEnemy`. */
  readonly inflicts?: readonly Affliction[] | undefined;
}

/** O esquadrão do jogador, com os personagens do Saga de Distortidus. */
export const PLAYER_SQUAD: readonly Recruit[] = [
  {
    name: 'Distortidus', className: 'Arruaceiro', weaponName: 'Adaga de Mithril',
    portrait: { sheet: 'Distortidus', index: 0 }, sprite: { sheet: 'distortidus', index: 0 },
    battler: 'distortidus', commander: true,
  },
  {
    name: 'Mondona', className: 'Artista Marcial', weaponName: 'Machado de Batalha',
    portrait: { sheet: 'Mondona', index: 0 }, sprite: { sheet: 'mondona', index: 0 },
    battler: 'mondona',
  },
  {
    name: 'Lucretia', className: 'Mago', weaponName: 'Varinha Mágica',
    portrait: { sheet: 'Actor1', index: 5 }, sprite: { sheet: 'Actor1', index: 5 },
    battler: 'Actor1_6',
  },
  {
    name: 'Dulpbuk', className: 'Cavaleiro', weaponName: 'Lança Longa',
    // Índice 2, como na ficha do acervo — o 1 é o de Clodz.
    portrait: { sheet: 'Distortidus', index: 2 }, sprite: { sheet: 'distortidus', index: 2 },
    battler: 'Dulpbuk',
  },
];

export const ENEMY_SQUAD: readonly Recruit[] = [
  {
    name: 'Soldado rebelde', className: 'Espadachim', weaponName: 'Espada Longa',
    portrait: { sheet: 'People1', index: 0 }, sprite: { sheet: 'People1', index: 0 },
    battler: 'Mercenary', aiProfile: 'agressivo',
  },
  {
    name: 'Ultra Goblin', className: 'Artista Marcial', weaponName: 'Machadinha',
    portrait: { sheet: 'Monster', index: 0 }, sprite: { sheet: 'Monster', index: 0 },
    battler: 'Goblin', aiProfile: 'cacador',
  },
  {
    name: 'Hacker Warrior', className: 'Arqueiro', weaponName: 'Arco Longo',
    portrait: { sheet: 'People2', index: 2 }, sprite: { sheet: 'People2', index: 2 },
    battler: 'Darkelf', aiProfile: 'agressivo',
  },
  {
    name: 'Tenente Xubléolus', className: 'Cavaleiro', weaponName: 'Lança Longa',
    portrait: { sheet: 'Evil', index: 0 }, sprite: { sheet: 'Evil', index: 0 },
    battler: 'Blackknight', commander: true, aiProfile: 'chefe',
  },
];

/**
 * Os quatro papéis do lado da CPU num mapa sem anotação.
 *
 * O papel é tático — classe, arma e comportamento —, e é ele que faz a
 * escaramuça ser uma batalha e não uma fila de bichos iguais: alguém que
 * avança, alguém que caça o alvo frágil, alguém que atira de longe e um chefe
 * que segura o posto. QUEM preenche cada papel vem do bestiário do acervo.
 */
const ENEMY_ROLES: readonly {
  readonly className: string;
  readonly weaponName: string;
  readonly aiProfile: AiProfile;
  readonly commander?: boolean;
}[] = [
  { className: 'Espadachim', weaponName: 'Espada Longa', aiProfile: 'agressivo' },
  { className: 'Artista Marcial', weaponName: 'Machadinha', aiProfile: 'cacador' },
  { className: 'Arqueiro', weaponName: 'Arco Longo', aiProfile: 'agressivo' },
  { className: 'Cavaleiro', weaponName: 'Lança Longa', aiProfile: 'chefe', commander: true },
];

/**
 * Quantos inimigos cada dificuldade põe em campo.
 *
 * A dificuldade mexia só na cabeça da IA — a mesma tropa, jogando melhor ou
 * pior. Isso resolve metade do problema: contra quatro goblins, uma IA
 * brilhante ainda perde para um esquadrão de quatro heróis, e o "difícil" não
 * era difícil. Número de inimigos é a outra metade, e é a que se sente.
 */
const ENEMY_COUNT: Record<AiDifficulty, number> = { facil: 3, normal: 5, dificil: 8 };

/** Quanto o lado da CPU se adianta no mapa. O difícil nasce mais perto. */
const ENEMY_PRESSURE: Record<AiDifficulty, number> = { facil: 0, normal: 1, dificil: 3 };

export function enemyCountFor(difficulty: AiDifficulty): number {
  return ENEMY_COUNT[difficulty] ?? ENEMY_COUNT['normal'];
}

/**
 * Um sorteio reprodutível a partir de uma semente.
 *
 * Nem `Math.random` nem o RNG do motor: `Math.random` faria o mesmo mapa abrir
 * diferente a cada montagem de componente do React — e o React monta mais de
 * uma vez —, e o RNG do motor pertence à partida, cujo curso não pode depender
 * de quem sorteou o elenco antes de ela começar. Esta é a mistura de bits
 * clássica (splitmix32), estável e barata.
 */
function shuffler(seed: number): () => number {
  let state = (Math.abs(Math.floor(seed)) || 1) >>> 0;
  return () => {
    state = (state + 0x9e3779b9) >>> 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return ((z ^ (z >>> 15)) >>> 0) / 4294967296;
  };
}

/** A lista embaralhada, sem repetir e sem tocar na original. */
function shuffled<T>(items: readonly T[], roll: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(roll() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * O esquadrão inimigo montado com os monstros do acervo.
 *
 * Os mapas do acervo não trazem tropa anotada, e o lado da CPU era uma lista
 * fixa de quatro nomes — três deles humanos, com folha de aldeão. Num jogo em
 * que o bestiário tem dezenas de criaturas, abrir um mapa e encontrar dois
 * camponeses e um soldado é a promessa errada.
 *
 * A escolha é determinística: a mesma semente devolve o mesmo esquadrão, senão
 * um replay gravado abriria com outros bichos. O que mudou é que a semente
 * deixou de ser uma constante no código da tela — cada escaramuça sorteia a
 * sua, e por isso dois testes seguidos no mesmo mapa trazem bichos diferentes.
 */
function enemyWeaponFor(roleWeapon: string, className: string, baseLevel?: number): string {
  if (baseLevel !== undefined && baseLevel <= 2) {
    switch (className) {
      case 'Espadachim': return 'Espada Curta';
      case 'Artista Marcial': return 'Machadinha';
      case 'Arqueiro': return 'Arco Curto';
      case 'Cavaleiro': return 'Lança Curta';
      default: return 'Espada Curta';
    }
  }
  return roleWeapon;
}

export function monsterSquad(
  enemies: readonly AcervoEnemy[],
  seed: number,
  count: number = ENEMY_ROLES.length,
  baseLevel?: number,
): Recruit[] {
  const usable = enemies.filter((e) => e.battler);
  if (usable.length === 0) return [...ENEMY_SQUAD];

  const roll = shuffler(seed);
  // Sorteados sem repetição enquanto o bestiário der: seis cópias do mesmo
  // goblin é uma tropa mais pobre do que seis bichos diferentes, e o acervo
  // tem dezenas.
  const picked = shuffled(usable, roll);
  const totalCount = Math.max(1, count);
  const commanderIndex =
    totalCount < ENEMY_ROLES.length ? totalCount - 1 : ENEMY_ROLES.length - 1;

  return Array.from({ length: totalCount }, (_, index) => {
    // Os quatro papéis se repetem em ciclo quando a tropa passa de quatro: o
    // quinto inimigo volta a ser espadachim, o sexto artista marcial. O chefe
    // é sempre um só — é ele o objetivo da partida.
    const role = ENEMY_ROLES[index % ENEMY_ROLES.length]!;
    const commander = index === commanderIndex;
    const beast = picked[index % picked.length]!;
    const sprite = beast.mapSprite;
    // A aparência genérica continua embaixo como rede: um battler que sumiu do
    // acervo deixaria a criatura invisível no tabuleiro e sem cara na ficha.
    const look = defaultLook('inimigo', index);
    const lvl = Math.max(1, commander ? (baseLevel ?? 1) + 1 : (baseLevel ?? 1));
    const inflicts = cleanAfflictions(beast.inflicts);
    return {
      name: beast.name,
      className: role.className,
      weaponName: enemyWeaponFor(role.weaponName, role.className, baseLevel),
      portrait: look.portrait,
      sprite: sprite ?? look.sprite,
      battler: beast.battler,
      ...(beast.size ? { size: beast.size } : {}),
      // O papel é tático, a peçonha é do bicho: o gnomo envenena qualquer que
      // seja a arma que o papel lhe deu.
      ...(inflicts ? { inflicts } : {}),
      battlerOnMap: !sprite,
      battlerAsPortrait: true,
      // O chefe mantém o comportamento de chefe; o resto herda o do bestiário.
      aiProfile: commander
        ? 'chefe'
        : role.aiProfile === 'chefe'
          ? 'agressivo'
          : toAiProfile(beast.aiProfile ?? role.aiProfile),
      ...(commander ? { commander: true } : {}),
      level: lvl,
    };
  });
}

/**
 * O que a unidade leva na bolsa.
 *
 * Um Fire Emblem não se joga com uma arma só: trocar de arma antes de atacar é
 * metade da tática — é assim que se responde ao triângulo, se alcança quem está
 * a duas casas, ou se poupa a arma boa para o chefe. O motor já sabia trocar
 * (`trocar_arma` é ação livre desde a Etapa 2); faltava alguém dar a segunda arma.
 *
 * A escolha é conservadora: a arma titular, mais uma alternativa de alcance
 * diferente do mesmo tipo que a classe sabe usar, e um bastão de cura para quem
 * cura. Nada aqui inventa item que não exista no acervo.
 */
function buildInventory(
  klass: ClassDef,
  primary: WeaponDef,
  catalog: readonly WeaponDef[] = [],
  items: ReadonlyMap<number, ItemDef> = new Map(),
): ItemInstance[] {
  const bag: ItemInstance[] = [{ weaponId: primary.id, usesLeft: primary.uses }];
  const usable = catalog.filter((w) => klass.weaponTypes.includes(w.type) && w.id !== primary.id);

  const add = (weapon: WeaponDef | undefined): void => {
    if (weapon && !bag.some((it) => it.weaponId === weapon.id)) {
      bag.push({ weaponId: weapon.id, usesLeft: weapon.uses });
    }
  };

  // Uma alternativa que alcance o que a titular não alcança.
  add(
    usable.find(
      (w) => !w.heals && (w.range[1] > primary.range[1] || w.range[0] < primary.range[0]),
    ),
  );
  // Uma reserva mais barata, para não gastar a boa em inimigo fraco.
  add(usable.find((w) => !w.heals && w.might < primary.might));

  // Um bastão de cura para quem cura
  if (!primary.heals && klass.weaponTypes.includes('cajado')) {
    add(usable.find((w) => w.heals));
  }

  // Poção / item de cura garantido para todos os combatentes poderem testar cura nos mapas
  const potion =
    catalog.find((w) => w.id === 50) ??
    catalog.find((w) => w.heals && w.name.toLowerCase().includes('poção')) ??
    catalog.find((w) => w.heals);
  if (potion) add(potion);

  /*
   * E um remédio, se ainda couber.
   *
   * Os monstros do acervo envenenam, paralisam e queimam — e na escaramuça a
   * bolsa é montada aqui, não pela campanha: sem isto o jogador conheceria as
   * condições sem nunca ter como tirá-las. O escolhido é o que rende mais
   * usos; entre iguais, o que cura menos coisas (o específico antes do
   * cura-tudo) e, por fim, o de menor id, para a escolha ser sempre a mesma.
   */
  if (bag.length < MAX_INVENTORY) {
    const remedy = [...items.values()]
      .filter((item) => (item.cures?.length ?? 0) > 0)
      .sort((a, b) =>
        b.uses - a.uses ||
        (a.cures?.length ?? 0) - (b.cures?.length ?? 0) ||
        a.id - b.id,
      )[0];
    if (remedy) bag.push({ itemId: remedy.id, usesLeft: remedy.uses });
  }

  return bag.slice(0, MAX_INVENTORY);
}

/**
 * A bolsa inicial de um personagem do acervo — a mesma regra do servidor
 * (`RosterController::recruit`), para a campanha jogar com a bolsa que o painel
 * edita e que o esquadrão guarda:
 *
 *   1. a titular: a primeira arma da ficha que a classe usa e que não cura;
 *      sem nenhuma, a primeira do acervo que a classe usa;
 *   2. as demais armas da ficha, na ordem;
 *   3. a poção tática (arma 50, ou o primeiro consumível), se couber e a bolsa
 *      ainda não tiver o que beber;
 *   4. no máximo `MAX_INVENTORY` itens.
 *
 * A escaramuça não usa esta regra: é teste, e monta a bolsa sozinha a partir
 * da titular (`buildInventory`).
 */
export function startingBag(
  equipment: readonly number[] | undefined,
  klass: ClassDef,
  weapons: readonly WeaponDef[],
): { weaponId: number; usesLeft: number }[] {
  const byId = new Map(weapons.map((w) => [w.id, w]));
  const sheet = [...new Set(equipment ?? [])]
    .map((id) => byId.get(id))
    .filter((w): w is WeaponDef => w !== undefined);
  const usable = (w: WeaponDef): boolean => klass.weaponTypes.includes(w.type) && !w.heals;

  const titular = sheet.find(usable) ?? [...weapons].sort((a, b) => a.id - b.id).find(usable);
  const order = [...(titular ? [titular] : []), ...sheet.filter((w) => w !== titular)];

  if (!order.some(isConsumable) && order.length < MAX_INVENTORY) {
    const potion = (byId.get(50) && isConsumable(byId.get(50)) ? byId.get(50) : undefined)
      ?? [...weapons].sort((a, b) => a.id - b.id).find(isConsumable);
    if (potion) order.push(potion);
  }

  return order.slice(0, MAX_INVENTORY).map((w) => ({ weaponId: w.id, usesLeft: Math.max(1, w.uses) }));
}

export interface UnitPresentation {
  readonly portrait: { sheet: string; index: number };
  readonly sprite: { sheet: string; index: number };
  readonly battler: string;
  /**
   * O desenho de combate faz as vezes de sprite de mapa.
   *
   * O RPG Maker não dá folha de caminhada a inimigo — lá o monstro só existe
   * na tela de batalha —, e num tático ele precisa andar pelo tabuleiro. Sem
   * isto, todo goblin, dragão e esqueleto importado entrava em campo com o
   * mesmo aldeão genérico, e o mapa virava uma fila de sósias. Quando o
   * monstro não tem folha própria, quem vai ao tabuleiro é o battler dele.
   */
  readonly battlerOnMap?: boolean;
  /**
   * O desenho de combate também faz as vezes de retrato, na ficha da unidade.
   *
   * Um monstro não tem rosto no acervo — as folhas de retrato são de gente. A
   * ficha caía num rosto genérico qualquer, e o jogador via a cara de um
   * aldeão ao inspecionar um dragão. Quem não tem retrato mostra a si mesmo.
   */
  readonly battlerAsPortrait?: boolean;
  /**
   * De qual personagem do acervo esta unidade é.
   *
   * É a linha de volta: quando o capítulo acaba, é por ela que o nível e a
   * bolsa de cada herói encontram o registro certo no esquadrão do jogador.
   * Ausente numa escaramuça, que não guarda nada.
   */
  readonly characterId?: number;
}

/**
 * Uma unidade do esquadrão persistente do jogador.
 *
 * O que a campanha guarda entre um capítulo e o outro. Ver o `RosterController`
 * do lado do servidor e a migração 0008.
 */
export interface RosterUnit {
  readonly characterId: number;
  readonly level: number;
  readonly exp: number;
  readonly stats?: Readonly<Record<string, number>> | undefined;
  /** Armas (`weaponId`) e itens do RPG (`itemId`), com os usos que sobraram. */
  readonly inventory?: readonly ItemInstance[] | undefined;
}

/** Busca o esquadrão guardado. Sem sessão, ou sem servidor, volta vazio. */
export async function loadRoster(): Promise<readonly RosterUnit[]> {
  try {
    const res = await fetch('/api/perfil/esquadrao', { credentials: 'include' });
    if (!res.ok) return [];
    const data = (await res.json()) as { units?: RosterUnit[] };
    return data.units ?? [];
  } catch {
    // Sem servidor, a campanha ainda joga — com o esquadrão no nível inicial
    // do acervo. Perder a partida por causa de uma requisição seria pior.
    return [];
  }
}

/**
 * Guarda o esquadrão depois de um capítulo da campanha.
 *
 * Só a campanha chama. Falhar aqui não estraga a vitória do jogador — a tela
 * de desfecho já está na frente dele —, e o servidor recusa qualquer regressão,
 * então a próxima gravação conserta o que esta perdeu.
 */
export async function saveRoster(units: readonly RosterUnit[]): Promise<void> {
  if (units.length === 0) return;
  try {
    await fetch('/api/perfil/esquadrao', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ units }),
    });
  } catch {
    // Ver acima: o progresso não se perde, ele só chega no próximo capítulo.
  }
}

/**
 * O esquadrão do jogador para a campanha e para o desafio online.
 *
 * Aqui não há sorteio: são OS personagens do jogador, no nível em que a
 * campanha os deixou. Quem ainda não entrou em campo nenhuma vez não tem
 * registro guardado, e entra no nível inicial da ficha do acervo — que é
 * exatamente o que se espera de um recruta novo.
 */
export function personalSquad(
  loaded: SkirmishContent,
  roster: readonly RosterUnit[],
  size: number,
  allowedCharacterIds?: readonly number[],
): readonly Recruit[] {
  const { classes, weapons } = loaded;
  const characters = (loaded.roster ?? []) as readonly (AcervoCharacter & {
    classId?: number; level?: number; equipment?: number[];
  })[];
  if (characters.length === 0) return PLAYER_SQUAD.slice(0, Math.max(1, size));

  const saved = new Map(roster.map((u) => [u.characterId, u]));

  // Filtra as unidades do jogador que pertencem ao seu esquadrão salvo (roster)
  const owned = characters.filter((c) => saved.has(c.id));

  // Aplica a restrição de personagens permitidos (ex: no modo história por capítulo)
  const eligible = owned.filter((c) => {
    if (allowedCharacterIds && allowedCharacterIds.length > 0) {
      return allowedCharacterIds.includes(c.id);
    }
    return true;
  });

  // Se o jogador tiver unidades elegíveis possuídas, usa estritamente elas.
  // Se o esquadrão salvo estiver vazio (ex: jogando deslogado), usa o acervo respeitando allowedCharacterIds
  const pool = eligible.length > 0
    ? eligible
    : (characters.filter((c) => !allowedCharacterIds || allowedCharacterIds.length === 0 || allowedCharacterIds.includes(c.id)));

  if (pool.length === 0) {
    return PLAYER_SQUAD.slice(0, Math.max(1, size));
  }

  const hasLord = pool.some((c) => c.name === 'Distortidus' || c.id === 1);

  const ordered = [...pool].sort((a, b) => {
    // Distortidus é o lorde protagonista: sempre vem primeiro no esquadrão
    const aIsLord = a.name === 'Distortidus' || a.id === 1;
    const bIsLord = b.name === 'Distortidus' || b.id === 1;
    if (aIsLord && !bIsLord) return -1;
    if (!aIsLord && bIsLord) return 1;

    const sa = saved.get(a.id);
    const sb = saved.get(b.id);
    const la = sa ? sa.level * 100 + sa.exp : 0;
    const lb = sb ? sb.level * 100 + sb.exp : 0;
    return lb - la || a.id - b.id;
  });

  // Não seleciona mais do que o número de heróis permitidos/disponíveis no pool
  const maxUnits = Math.min(Math.max(1, size), ordered.length);

  return ordered.slice(0, maxUnits).map((record, index) => {
    const klass = classes.find((k) => k.id === record.classId) ?? classes[0]!;
    const memory = saved.get(record.id);
    // A bolsa guardada vence; sem ela, a bolsa inicial da ficha — a mesma que o
    // servidor grava ao recrutar.
    const bag: readonly ItemInstance[] = memory?.inventory && memory.inventory.length > 0
      ? memory.inventory
      : startingBag(record.equipment, klass, weapons);
    // A titular é a primeira ARMA da bolsa: um antídoto no primeiro espaço não
    // é o que a unidade empunha.
    const titular = weapons.find((w) => w.id === bag.find(isWeaponSlot)?.weaponId)
      ?? weapons.find((w) => klass.weaponTypes.includes(w.type) && !w.heals)
      ?? weapons[0]!;
    const look = defaultLook('jogador', index);
    const isLord = record.name === 'Distortidus' || record.id === 1;
    const isCommander = hasLord ? isLord : index === 0;

    return {
      name: record.name,
      className: klass.name,
      weaponName: titular.name,
      portrait: record.portrait ?? look.portrait,
      sprite: record.mapSprite ?? look.sprite,
      battler: record.battler ?? look.battler,
      // O nível guardado vence o da ficha: é ele que a campanha construiu.
      level: Math.max(1, memory?.level ?? record.level ?? 1),
      characterId: record.id,
      ...(memory?.stats ? { stats: memory.stats } : {}),
      inventory: bag,
      ...(isCommander ? { commander: true } : {}),
    } satisfies Recruit;
  });
}

/**
 * O esquadrão do jogador, sorteado entre os personagens do acervo.
 *
 * Cada herói entra com a classe da ficha dele e a arma que ela equipa — não
 * com um papel genérico —, porque é isso que torna o sorteio um teste de
 * verdade: um esquadrão com dois arqueiros e nenhum curandeiro joga um mapa de
 * um jeito completamente diferente de um com dois cavaleiros.
 *
 * A lista fixa do código continua de reserva, para quando o acervo não veio —
 * o editor e os testes montam escaramuças sem passar pela API.
 */
export function randomSquad(
  loaded: SkirmishContent,
  seed: number,
  size: number,
): readonly Recruit[] {
  const roster = loaded.roster ?? [];
  const { classes, weapons } = loaded;

  const usable = roster.filter((c) => classes.some((k) => k.id === (c as { classId?: number }).classId));
  if (usable.length === 0) return PLAYER_SQUAD.slice(0, Math.max(1, size));

  const roll = shuffler(seed ^ 0x5f3a);
  const picked = shuffled(usable, roll).slice(0, Math.max(1, size));

  const squad = picked.map((c, index) => {
    const record = c as AcervoCharacter & { classId?: number; level?: number; equipment?: number[] };
    const klass = classes.find((k) => k.id === record.classId) ?? classes[0]!;
    // A arma da ficha, se ela existir no acervo e a classe souber usá-la;
    // senão a primeira que a classe sabe usar. Dar um arco a um cavaleiro
    // produz uma unidade que não consegue atacar, e o teste não testa nada.
    const owned = (record.equipment ?? [])
      .map((id) => weapons.find((w) => w.id === id))
      .find((w): w is WeaponDef => Boolean(w) && klass.weaponTypes.includes(w!.type));
    const fallback =
      weapons.find((w) => klass.weaponTypes.includes(w.type) && !w.heals) ?? weapons[0]!;
    const weapon = owned ?? fallback;
    const look = defaultLook('jogador', index);

    return {
      name: record.name,
      className: klass.name,
      weaponName: weapon.name,
      portrait: record.portrait ?? look.portrait,
      sprite: record.mapSprite ?? look.sprite,
      battler: record.battler ?? look.battler,
      level: Math.max(1, record.level ?? 1),
      // O primeiro da lista comanda: o objetivo "derrotar o comandante" precisa
      // de um dos dois lados marcado, e sem isso a partida não tem como acabar.
      ...(index === 0 ? { commander: true } : {}),
    } satisfies Recruit;
  });

  return squad;
}

export interface Skirmish {
  readonly state: MatchState;
  readonly presentation: ReadonlyMap<number, UnitPresentation>;
}

/**
 * A aparência que a partida dá a quem o mapa não vestiu.
 *
 * Um mapa pode declarar só nome, classe e casa — é o que o editor semeia — e
 * alguém tem de decidir com que cara essa unidade entra em campo. Fica exposto
 * porque o editor precisa da MESMA resposta: mostrar um disco com a inicial da
 * classe onde o jogo vai pôr um soldado é montar o mapa às cegas.
 */
export function defaultLook(
  team: Team,
  index: number,
): {
  readonly portrait: { sheet: string; index: number };
  readonly sprite: { sheet: string; index: number };
  readonly battler: string;
} {
  // O aliado veste o time da casa: ele luta do lado do jogador.
  return team === 'inimigo'
    ? {
        portrait: { sheet: 'People1', index: index % 8 },
        sprite: { sheet: 'People1', index: index % 8 },
        battler: 'Mercenary',
      }
    : {
        portrait: { sheet: 'Distortidus', index: index % 8 },
        sprite: { sheet: 'distortidus', index: index % 8 },
        battler: 'distortidus',
      };
}

/**
 * Traz para o campo de batalha quem foi posto fora dele.
 *
 * O mapa anotado à mão diz onde cada unidade começa, e a partida obedecia sem
 * conferir. Uma casa aberta cercada por quatro paredes é uma casa aberta: o
 * comandante nascia lá e passava a partida inteira sem uma única jogada
 * possível — e como o objetivo era derrotá-lo, também não havia como a partida
 * terminar. Foi assim que um mapa importado saiu do editor com o comandante do
 * jogador numa ilha de uma casa só.
 *
 * A regra é a mesma que já vale para quem nasce sem anotação: dentro do campo
 * de batalha, que é a área de onde se alcança o resto do mapa — a pé ou por
 * portal. Quem já está numa casa boa não é movido; quem não está vai para a
 * casa livre mais próxima da que o autor pediu, que é a leitura mais fiel da
 * intenção dele que ainda deixa a unidade jogar.
 */
export function settleOnBattlefield(
  map: Ground,
  wanted: readonly (Vec2 | undefined)[],
): Vec2[] {
  const region = battleground(map);
  const settled: Vec2[] = [];
  const taken = new Set<string>();
  const stranded: number[] = [];

  for (const [index, at] of wanted.entries()) {
    const tile = at ? at.y * map.width + at.x : -1;
    // Duas unidades na mesma casa também é ninguém podendo jogar: a segunda
    // entra dentro da primeira e as duas ficam onde estão.
    if (at && region.has(tile) && !taken.has(`${at.x},${at.y}`)) {
      taken.add(`${at.x},${at.y}`);
      settled[index] = at;
    } else {
      stranded.push(index);
    }
  }

  for (const index of stranded) {
    const wish = wanted[index] ?? { x: 0, y: 0 };
    let best: Vec2 | null = null;
    let bestDistance = Infinity;

    for (const tile of region) {
      const x = tile % map.width;
      const y = Math.floor(tile / map.width);
      if (taken.has(`${x},${y}`)) continue;
      const distance = Math.abs(x - wish.x) + Math.abs(y - wish.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = { x, y };
      }
    }

    // Sem campo de batalha nenhum não há para onde levar — o mapa já não dava
    // partida antes desta função existir, e mudar a casa não o conserta.
    if (best) taken.add(`${best.x},${best.y}`);
    settled[index] = best ?? wish;
  }

  return settled;
}

/**
 * Onde os dois lados nascem, num mapa que não diz onde.
 *
 * Mora aqui, e não dentro da partida, porque o editor de mapas precisa da
 * mesma resposta: é ele quem propõe as duas forças de um mapa do acervo que
 * chega sem anotação nenhuma, e propor num critério diferente do da partida foi
 * exatamente como um comandante acabou plantado numa casa fechada por quatro
 * paredes.
 */
export function planDeployment(
  map: Ground,
  playerCount: number,
  enemyCount: number,
  /**
   * O que faz duas partidas no mesmo mapa começarem diferentes.
   *
   * `seed` gira as âncoras em torno do centro: os dois lados continuam em
   * cantos opostos e à mesma distância um do outro — o que o desenho garante —
   * mas em cantos DIFERENTES a cada partida. `pressure` puxa o lado da CPU na
   * direção do jogador, e é o que faz o "difícil" chegar antes.
   */
  options: { readonly seed?: number | undefined; readonly pressure?: number | undefined } = {},
): { readonly player: readonly Vec2[]; readonly enemy: readonly Vec2[] } {
  const cx = Math.floor(map.width / 2);
  const cy = Math.floor(map.height / 2);
  const isLarge = map.width > 16 || map.height > 16;

  // Afastamento moderado das âncoras para dar espaço tático sem prolongar demais o trajeto
  const offsetDistance = Math.min(7, Math.floor(Math.min(map.width, map.height) * 0.28));

  /*
   * Quatro posições de partida, escolhidas pela semente.
   *
   * Girar em torno do centro, e não sortear casa a casa: o que faz um começo
   * ser jogável é os dois lados nascerem opostos e separados, e um sorteio
   * livre desmancharia justamente isso. Quatro quadrantes é variedade bastante
   * para o mesmo mapa não repetir a mesma abertura.
   */
  const corner = ((Math.abs(Math.floor(options.seed ?? 0)) % 4) + 4) % 4;
  const dirs: readonly Vec2[] = [
    { x: -1, y: 1 }, { x: 1, y: 1 }, { x: 1, y: -1 }, { x: -1, y: -1 },
  ];
  const side = dirs[corner]!;
  const clampX = (x: number): number => Math.max(2, Math.min(map.width - 3, x));
  const clampY = (y: number): number => Math.max(2, Math.min(map.height - 3, y));

  const wantedPlayerAnchor: Vec2 = isLarge
    ? { x: clampX(cx + side.x * offsetDistance), y: clampY(cy + side.y * offsetDistance) }
    : { x: clampX(side.x < 0 ? 2 : map.width - 3), y: clampY(side.y > 0 ? map.height - 3 : 2) };

  // O lado da CPU nasce no canto oposto, adiantado pela pressão da dificuldade.
  const pressure = Math.max(0, options.pressure ?? 0);
  const wantedEnemyAnchor: Vec2 = isLarge
    ? {
        x: clampX(cx - side.x * Math.max(1, offsetDistance - pressure)),
        y: clampY(cy - side.y * Math.max(1, offsetDistance - pressure)),
      }
    : { x: clampX(side.x < 0 ? map.width - 3 : 2), y: clampY(side.y > 0 ? 2 : map.height - 3) };

  // Os dois lados nascem na mesma área contígua, sempre. Antes as âncoras eram
  // puramente geométricas, e num mapa partido cada esquadrão podia cair num
  // cômodo diferente — a partida abria e nunca chegava a acontecer.
  const region = battleground(map);

  // E cada esquadrão nasce numa ilha só, com espaço para se desdobrar: o campo
  // conta os portais, e plantar quatro recrutas numa alcova do outro lado de um
  // teleporte abre o turno com todo mundo se trancando. Ver `squadGround`.
  const playerGround = squadGround(map, region, wantedPlayerAnchor, playerCount);
  const playerAnchor = nearestInRegion(map, playerGround, wantedPlayerAnchor) ?? wantedPlayerAnchor;

  // O canto oposto é a intenção; a separação mínima é a garantia. Numa área
  // apertada os dois cantos geométricos podem apontar para o mesmo punhado de
  // casas, e os esquadrões começariam encostados um no outro. A busca é sobre o
  // campo inteiro — o afastamento vale atravessando portal — e só depois o
  // resultado é puxado para uma ilha em que o esquadrão caiba.
  // A separação mínima também cede à pressão: no difícil os dois lados começam
  // mais perto, e a primeira troca de golpes vem antes.
  const separation = Math.max(4, offsetDistance * 2 - pressure * 2);
  const enemyTarget =
    farthestInRegion(map, region, playerAnchor, wantedEnemyAnchor, separation) ?? wantedEnemyAnchor;
  const enemyGround = squadGround(map, region, enemyTarget, enemyCount);
  const enemyAnchor = nearestInRegion(map, enemyGround, enemyTarget) ?? enemyTarget;

  const player = deployment(map, playerAnchor, playerCount, [], playerGround, region);
  const enemy = deployment(map, enemyAnchor, enemyCount, player, enemyGround, region);

  return { player, enemy };
}

/**
 * De que tamanho é o desenho de combate de um battler, em pixels.
 *
 * O motor não conhece o acervo e não deve conhecer: quem sabe medir uma imagem
 * é o manifesto, que vive do lado do navegador. A escaramuça recebe a régua de
 * fora e a usa para decidir quantas casas cada monstro ocupa.
 */
export type BattlerRuler = (battler: string) => { width: number; height: number } | null;

/**
 * O corpo cabe aqui, com o chão livre e sem ninguém por baixo?
 *
 * Repete a conta do motor (`bodyFits`) sobre o mapa cru porque isto acontece
 * ANTES de existir partida: as unidades ainda estão sendo montadas, e não há
 * `MatchState` para consultar.
 */
function bodyClear(
  map: Ground,
  at: Vec2,
  size: UnitSize,
  taken: ReadonlySet<number>,
): boolean {
  for (const tile of footprint(at, size)) {
    if (tile.x < 0 || tile.y < 0 || tile.x >= map.width || tile.y >= map.height) return false;
    const index = tile.y * map.width + tile.x;
    if (!isOpen(map, index)) return false;
    if (taken.has(index)) return false;
  }
  return true;
}

/**
 * Acomoda os corpos grandes depois de todo mundo já ter uma casa.
 *
 * O plantio de tropa é feito casa a casa, e não sabe que o dragão precisa de
 * quatro. Sem este passo, um monstro de duas por duas podia nascer com metade
 * dentro de um muro — e aí ele não tem para onde ir, porque nenhum destino
 * cabe, e passa a partida inteira parado. A busca é em espiral a partir da casa
 * pretendida; não achando nada, o bicho encolhe para uma casa, que é o tamanho
 * em que sempre coube.
 */
export function settleBodies(
  map: Ground,
  units: readonly Unit[],
): Unit[] {
  const SINGLE: UnitSize = { w: 1, h: 1 };
  const taken = new Set<number>();
  const claim = (at: Vec2, size: UnitSize): void => {
    for (const tile of footprint(at, size)) {
      if (tile.x >= 0 && tile.y >= 0 && tile.x < map.width && tile.y < map.height) {
        taken.add(tile.y * map.width + tile.x);
      }
    }
  };

  // Quem cabe numa casa fica onde está: o plantio já os colocou sem conflito, e
  // mexer neles só deslocaria o problema. As casas deles são reservadas antes,
  // para que os corpos grandes desviem — e não o contrário.
  for (const unit of units) {
    if (unit.size === undefined) claim(unit.position, SINGLE);
  }

  const reach = Math.max(map.width, map.height);

  return units.map((unit) => {
    const size = normalizeSize(unit.size);
    if (unit.size === undefined) return unit;

    if (bodyClear(map, unit.position, size, taken)) {
      claim(unit.position, size);
      return unit;
    }

    for (let radius = 1; radius <= reach; radius++) {
      let best: Vec2 | null = null;
      for (let dy = -radius; dy <= radius; dy++) {
        const budget = radius - Math.abs(dy);
        for (const dx of budget === 0 ? [0] : [-budget, budget]) {
          const at = { x: unit.position.x + dx, y: unit.position.y + dy };
          if (bodyClear(map, at, size, taken)) {
            best = at;
            break;
          }
        }
        if (best) break;
      }
      if (best) {
        claim(best, size);
        return { ...unit, position: best };
      }
    }

    // Nenhuma área serve neste mapa: o monstro entra do tamanho de sempre, e
    // pelo menos joga.
    claim(unit.position, SINGLE);
    const { size: _dropped, ...rest } = unit;
    return rest as Unit;
  });
}

/**
 * O que muda de uma escaramuça para a outra.
 *
 * Uma escaramuça é uma partida de TESTE — é para isso que ela existe —, e
 * testar sempre o mesmo confronto não testa nada. Abrir qualquer mapa e
 * encontrar os mesmos quatro heróis contra os mesmos quatro monstros, nas
 * mesmas casas, torna a tela um cenário fixo com plano de fundo variável.
 */
/** O que um baú guarda: uma arma do acervo, ou um item do RPG. Nunca os dois. */
type Prize = { readonly weaponId: number } | { readonly itemId: number };

/**
 * De quantos baús de suprimento um mapa de capítulo precisa.
 *
 * Pelo tamanho: um mapa grande é uma batalha longa, e uma batalha longa gasta
 * mais poção. Dois é o piso — um só numa ponta do mapa é um baú que metade das
 * partidas nunca alcança.
 */
export function supplyCount(map: Pick<MapData, 'width' | 'height'>): number {
  const tiles = Math.max(1, map.width * map.height);
  if (tiles >= 1200) return 4;
  if (tiles >= 600) return 3;
  return 2;
}

/**
 * Os baús que abastecem uma batalha do Modo História.
 *
 * Os mapas do acervo não trazem baú anotado — não trazem anotação nenhuma: nem
 * tropa, nem objetivo, nem tesouro. E um capítulo é longo: a poção da bolsa
 * inicial acaba no terceiro combate e a arma se parte antes do comandante, o
 * que deixava a campanha inteira dependendo da Loja entre um capítulo e outro.
 * O baú é a resposta que um Fire Emblem dá a isso — o suprimento está NO mapa,
 * e custa o desvio de ir buscá-lo.
 *
 * O que sai daqui é `MapChestData`: baú anotado, como se o mapa o trouxesse. O
 * mapa que traz os seus (feitos no editor) não passa por aqui — o desenho do
 * autor vence a mão automática, sempre.
 *
 * Determinístico pela semente, como todo o resto da montagem: a mesma partida
 * reaberta reencontra os mesmos baús nas mesmas casas.
 */
export function supplyChests(
  loaded: Pick<SkirmishContent, 'map' | 'weapons' | 'items'>,
  seed: number,
  count: number,
  options: {
    /** As casas onde alguém já nasce: baú debaixo de unidade é baú de graça. */
    readonly taken?: readonly Vec2[] | undefined;
    /**
     * Os tipos de arma que o esquadrão do jogador consegue empunhar.
     *
     * Sem isto o baú entrega o arco ao espadachim: um prêmio que ninguém pode
     * usar é um desvio cobrado por nada. Ausente, vale o acervo inteiro — é o
     * que os testes e o editor passam.
     */
    readonly weaponTypes?: ReadonlySet<string> | undefined;
  } = {},
): MapChestData[] {
  const wanted = Math.max(0, Math.floor(count));
  if (wanted === 0) return [];

  const roll = shuffler(seed ^ 0x5c4e57);
  const defs = (loaded.items ?? []).map(toItemDef);

  /*
   * O que vai dentro, em três gostos que se alternam.
   *
   * Poção primeiro porque é o que falta: é ela que devolve o herói ferido à
   * linha de frente sem gastar o turno do curandeiro. Depois a arma, que é o
   * outro recurso que se gasta. E por fim o que tira condição — o antídoto que
   * ninguém carrega antes de precisar.
   */
  const healers = defs.filter((def) => (def.heal ?? 0) > 0 || (def.healRate ?? 0) > 0);
  /*
   * A poção de PV fixos vem antes da que enche a barra inteira.
   *
   * Um frasco de cura total achado no chão desfaz a batalha que o capítulo
   * montou; a poção de dez PV faz o jogador escolher a hora de beber, que é o
   * que a torna interessante. As frações só entram quando o acervo não tem
   * nenhuma de valor fixo.
   */
  const fixed = healers.filter((def) => (def.heal ?? 0) > 0);
  const potions: Prize[] = (fixed.length > 0 ? fixed : healers)
    // Mais usos primeiro: a poção tática de três goles vale três frascos de um.
    .sort((a, b) => b.uses - a.uses || (a.heal ?? 0) - (b.heal ?? 0) || a.id - b.id)
    .map((def) => ({ itemId: def.id }));
  const cures: Prize[] = defs
    .filter((def) => (def.cures?.length ?? 0) > 0)
    .sort((a, b) => b.uses - a.uses || a.id - b.id)
    .map((def) => ({ itemId: def.id }));

  /*
   * Uma arma inteira, do degrau logo acima do que o esquadrão carrega.
   *
   * Fora as que curam (são cajado e poção tática, e o que se bebe já vem pela
   * lista de itens), as chaves — que fariam um baú entregar a chave de outro —
   * e as de poder zero, que os pacotes importados usam como cabeçalho de
   * categoria e não são arma nenhuma.
   */
  const usable = loaded.weapons.filter(
    (w) =>
      !w.heals && !w.opensChests && w.uses >= 20 && w.might > 0 &&
      (options.weaponTypes === undefined || options.weaponTypes.has(w.type)),
  );
  const ranked = [...usable].sort((a, b) => a.might - b.might || b.uses - a.uses || a.id - b.id);
  /*
   * A faixa do meio, e não o topo nem a base.
   *
   * O topo é a arma lendária, e achá-la no Capítulo 1 encerra a campanha. A
   * base é a arma com que o esquadrão já entrou, e um baú que devolve o que
   * você já tem não vale o turno gasto para abri-lo. A faixa é dita em
   * proporção da lista para valer em qualquer acervo — o do jogo, o de um
   * pacote importado, o de um teste com três armas.
   */
  const arms: Prize[] = (
    ranked.length <= 3
      ? ranked
      : ranked.slice(Math.floor(ranked.length * 0.3), Math.max(
          Math.floor(ranked.length * 0.3) + 1,
          Math.floor(ranked.length * 0.6),
        ))
  ).map((w) => ({ weaponId: w.id }));

  /*
   * Sorteia entre os primeiros da lista, e não entre todos.
   *
   * A ordem já é a da utilidade, e sortear no acervo inteiro traria a poção
   * mais fraca com a mesma frequência da melhor. Cinco é variedade bastante
   * para dois baús não virem iguais.
   */
  const pick = (pool: readonly Prize[]): Prize | undefined =>
    pool.length === 0 ? undefined : pool[Math.floor(roll() * Math.min(5, pool.length))];

  const loot = (index: number): Prize | undefined => {
    switch (index % 3) {
      case 0: return pick(potions) ?? pick(arms);
      case 1: return pick(arms) ?? pick(potions);
      default: return pick(cures) ?? pick(potions) ?? pick(arms);
    }
  };

  const spots = supplySpots(loaded.map, seed, wanted, options.taken ?? []);
  const chests: MapChestData[] = [];
  for (let index = 0; index < spots.length; index++) {
    const prize = loot(index);
    if (!prize) break;
    /*
     * Destrancado, e de propósito.
     *
     * O baú trancado é o do Fire Emblem: exige a chave guardada ou o ladrão do
     * esquadrão, e é assim que continua o baú que o autor desenha no editor.
     * Mas este aqui é suprimento plantado pela partida, e trancá-lo faria a
     * campanha inteira depender de uma chave que ninguém tem — quatro baús de
     * enfeite num mapa em que a poção acabou. Abrir ainda custa: a unidade tem
     * de parar em cima dele e gastar a jogada.
     */
    chests.push({ at: spots[index]!, ...prize, locked: false });
  }
  return chests;
}

/**
 * Onde os baús de suprimento cabem.
 *
 * Dentro do campo de batalha (a área contígua em que os dois lados nascem —
 * ver `battleground`), longe de quem já está plantado e espalhados entre si:
 * três baús no mesmo canto são um baú só, e um baú em cima da casa de partida
 * é um item que o jogador recolhe sem ter andado.
 */
function supplySpots(
  map: Ground,
  seed: number,
  count: number,
  taken: readonly Vec2[],
): Vec2[] {
  const region = [...battleground(map)];
  if (region.length === 0) return [];

  const far = (a: Vec2, b: Vec2): number => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  const candidates = shuffled(
    region.map((index) => ({ x: index % map.width, y: Math.floor(index / map.width) })),
    shuffler(seed ^ 0x7a11c3),
  );

  const chosen: Vec2[] = [];
  /*
   * Três passadas, cada uma exigindo menos afastamento que a anterior.
   *
   * Um mapa apertado não pode ficar sem baú por não caber a distância ideal —
   * mas a primeira tentativa é sempre a boa: quatro casas de quem já está em
   * campo. A última exige só que a casa esteja livre; dois baús nunca ficam
   * encostados, em passada nenhuma.
   */
  for (const minimum of [4, 2, 0]) {
    for (const spot of candidates) {
      if (chosen.length >= count) break;
      if (taken.some((t) => far(t, spot) < minimum)) continue;
      if (chosen.some((c) => far(c, spot) < Math.max(minimum, 2))) continue;
      if (taken.some((t) => far(t, spot) === 0)) continue;
      chosen.push(spot);
    }
    if (chosen.length >= count) break;
  }

  return chosen.slice(0, count);
}

export interface SkirmishOptions {
  /** Decide quantos inimigos entram e o quanto eles já começam adiantados. */
  readonly difficulty?: AiDifficulty | undefined;
  /** Nível base dos inimigos gerados (usado especialmente no Modo História). */
  readonly enemyLevel?: number | undefined;
  /**
   * O objetivo que vale nesta partida, acima do que o mapa declara.
   *
   * É por aqui que o capítulo manda: a campanha grava o objetivo no capítulo,
   * e o mesmo mapa pode ser "derrotar todos" num capítulo e "sobreviver cinco
   * turnos" em outro. Ausente, vale o do mapa.
   */
  readonly objective?: MapObjectiveData | null | undefined;
  /** Quantos heróis o jogador leva. Padrão: quatro. */
  readonly squadSize?: number | undefined;
  /**
   * Subir de nível vale nesta partida?
   *
   * Ausente é "sim", que é o que sempre valeu. A escaramuça passa `false`: ela
   * é um teste, e um teste que muda a ficha dos personagens deixa de ser
   * repetível — e pior, adulterava o esquadrão que a campanha guarda.
   */
  readonly progression?: boolean | 'jogador' | undefined;
  /**
   * O esquadrão do jogador, quando quem chama já sabe qual é.
   *
   * É por aqui que a campanha e o modo online entram: eles não sorteiam
   * elenco, eles levam os personagens do jogador, no nível em que estão.
   */
  readonly squad?: readonly Recruit[] | undefined;
  /**
   * Quem, do lado de lá, muda de lado se falarem com ele.
   *
   * É o recrutamento por conversa do capítulo. As unidades são encontradas
   * pelo NOME com que o mapa as planta — é o que o autor do capítulo enxerga
   * no editor de mapas —, e a conversa pode ser restrita a um personagem do
   * esquadrão (`byName`). Ver `ChapterRecruit`.
   */
  readonly recruits?: readonly ChapterRecruit[] | undefined;
  /**
   * A batalha planta baús de suprimento quando o mapa não traz nenhum?
   *
   * A campanha diz que sim. Um capítulo é longo, os mapas do acervo não trazem
   * tesouro anotado, e sem isto a poção da bolsa inicial acaba no terceiro
   * combate. Ver `supplyChests`. A escaramuça e o desafio online dizem que não:
   * a escaramuça é teste, e no online um tesouro sorteado premiaria quem
   * calhasse de nascer perto dele.
   */
  readonly supplies?: boolean | undefined;
}

export function buildSkirmish(
  loaded: SkirmishContent,
  seed: number,
  measure?: BattlerRuler,
  options: SkirmishOptions = {},
): Skirmish {
  const { classes, weapons, map } = loaded;
  const difficulty: AiDifficulty = options.difficulty ?? 'normal';
  const byClass = (name: string): ClassDef => {
    const found = classes.find((c) => c.name === name);
    if (found) return found;
    if (classes.length > 0 && classes[0]) return classes[0];
    throw new Error(`classe ausente no acervo: ${name}`);
  };
  const byWeapon = (name: string): WeaponDef => {
    const found = weapons.find((w) => w.name === name);
    if (found) return found;
    if (weapons.length > 0 && weapons[0]) return weapons[0];
    throw new Error(`arma ausente no acervo: ${name}`);
  };

  /*
   * O elenco de cada lado, sorteado por esta partida.
   *
   * O esquadrão do jogador era a mesma lista de quatro nomes em todo mapa. Com
   * o acervo de personagens carregado, a escaramuça passa a sortear entre
   * TODOS eles — o que muda o teste de verdade: classes diferentes, alcances
   * diferentes, problemas diferentes no mesmo terreno.
   */
  const playerSquad = options.squad ?? randomSquad(loaded, seed, options.squadSize ?? 4);
  const foeCount = enemyCountFor(difficulty);
  const { player: playerSpots, enemy: enemySpots } = planDeployment(
    map,
    playerSquad.length,
    foeCount,
    { seed, pressure: ENEMY_PRESSURE[difficulty] ?? 0 },
  );

  /**
   * Veste o recruta com a aparência que o acervo guarda para ele.
   *
   * Pelo nome, que é o que as duas listas têm em comum — a do código diz o
   * papel tático (classe, arma, comandante) e a do acervo diz a cara. Campo a
   * campo, e não o registro inteiro: um personagem gravado sem battler não pode
   * apagar o que a lista de reserva sabia.
   */
  const dressed = (recruit: Recruit): Recruit => {
    const sheet = (loaded.roster ?? []).find((c) => c.name === recruit.name);
    if (!sheet) return recruit;
    return {
      ...recruit,
      ...(sheet.portrait ? { portrait: sheet.portrait } : {}),
      ...(sheet.mapSprite ? { sprite: sheet.mapSprite } : {}),
      ...(sheet.battler ? { battler: sheet.battler } : {}),
    };
  };

  const presentation = new Map<number, UnitPresentation>();
  const units: Unit[] = [];
  let id = 1;

  /*
   * Só quem vai ao tabuleiro com o desenho de combate pode ser grande.
   *
   * Uma unidade com folha de caminhada é desenhada num quadro de 48 pixels,
   * do tamanho da casa, e não há nada nela que peça mais espaço. Sem régua —
   * o editor e os testes chamam `buildSkirmish` sem manifesto — ninguém cresce,
   * e o jogo é exatamente o que era.
   */
  const bodyOf = (recruit: Recruit): UnitSize | undefined => {
    /*
     * A escolha do painel vem primeiro, e vale mesmo para quem tem folha de
     * caminhada: um autor que diz "este monstro ocupa duas casas por duas"
     * está falando de regra de tabuleiro — o que ele bloqueia, o que o alcança,
     * como é cercado —, e isso não é assunto do tamanho do arquivo de imagem.
     */
    if (recruit.size) {
      const w = Math.max(1, Math.min(4, Math.floor(recruit.size.w)));
      const h = Math.max(1, Math.min(4, Math.floor(recruit.size.h)));
      return w === 1 && h === 1 ? undefined : { w, h };
    }
    if (!measure || !recruit.battlerOnMap || !recruit.battler) return undefined;
    const art = measure(recruit.battler);
    if (!art) return undefined;
    const box = mapArtFootprint(art.width, art.height);
    return box.w === 1 && box.h === 1 ? undefined : { w: box.w, h: box.h };
  };

  const enlist = (recruit: Recruit, team: Team, position: Vec2): void => {
    const klass = byClass(recruit.className);
    const weapon = byWeapon(recruit.weaponName);
    const size = bodyOf(recruit);
    presentation.set(id, {
      portrait: recruit.portrait,
      sprite: recruit.sprite,
      battler: recruit.battler,
      ...(recruit.battlerOnMap ? { battlerOnMap: true } : {}),
      ...(recruit.battlerAsPortrait ? { battlerAsPortrait: true } : {}),
      ...(recruit.characterId ? { characterId: recruit.characterId } : {}),
    });
    /*
     * O nível é o da ficha; os atributos são os que a campanha guardou.
     *
     * Sem a memória, a unidade entra nos atributos BASE da classe qualquer que
     * seja o nível — um Dulpbuk de nível 7 com a folha de um recruta. Com ela,
     * entra como o jogador a construiu, ganho a ganho. Quem nunca lutou cai em
     * `statsAtLevel`, que é o crescimento esperado sem sorteio — o mesmo que o
     * painel mostra na prévia.
     */
    const level = Math.max(1, recruit.level ?? 1);
    const stats = recruit.stats
      ? ({ ...statsAtLevel(klass, level), ...recruit.stats } as Stats)
      : statsAtLevel(klass, level);
    // A bolsa guardada vale, desde que cada coisa ainda exista no acervo — a
    // arma no arsenal, o item entre os que servem em campo.
    const remembered: ItemInstance[] = (recruit.inventory ?? []).flatMap((it): ItemInstance[] => {
      if (it.usesLeft <= 0) return [];
      if (it.weaponId !== undefined) {
        return weapons.some((w) => w.id === it.weaponId)
          ? [{ weaponId: it.weaponId, usesLeft: it.usesLeft }]
          : [];
      }
      return loaded.content.items.has(it.itemId) ? [{ itemId: it.itemId, usesLeft: it.usesLeft }] : [];
    });
    const inventory: ItemInstance[] = remembered.length > 0
      ? remembered
      : buildInventory(klass, weapon, weapons, loaded.content.items);
    const inflicts = cleanAfflictions(recruit.inflicts);

    units.push({
      id,
      name: recruit.name,
      team,
      classId: klass.id,
      level,
      exp: 0,
      stats,
      currentHp: stats.hp,
      position,
      // Ausente é o caso comum, e ausente tem de continuar SENDO ausente: o
      // estado é comparado byte a byte nos replays.
      ...(size ? { size } : {}),
      inventory,
      // A primeira arma da bolsa, que pode não ser o primeiro espaço.
      equipped: Math.max(0, inventory.findIndex(isWeaponSlot)),
      states: [],
      ...(inflicts ? { inflicts } : {}),
      hasActed: false,
      alive: true,
      aiProfile: recruit.aiProfile ?? 'agressivo',
      isCommander:
        recruit.commander ?? (team === 'jogador' && (recruit.name === 'Distortidus' || recruit.characterId === 1)),
      weaponExp: {},
    });
    id++;
  };

  const rawPlacements: readonly MapPlacementData[] = Array.isArray(map.placements) ? map.placements : [];
  const rawObjective = map.objective;

  // O editor oferece três lados e o motor entende os três: um aliado é uma
  // unidade da IA que luta ao seu lado — colapsá-lo em 'jogador' entregaria ao
  // jogador o controle de quem deveria agir sozinho.
  const teamOf = (p: MapPlacementData): Team =>
    p.team === 'inimigo' ? 'inimigo' : p.team === 'aliado' ? 'aliado' : 'jogador';

  /**
   * Põe em campo uma unidade ANOTADA NO MAPA, com a ficha que o acervo tem dela.
   *
   * Extraído para ser usado por dois caminhos: a escaramuça, que joga o mapa
   * inteiro como o autor o desenhou, e o capítulo, que aproveita as tropas
   * anotadas mas leva o esquadrão do jogador no lugar do lado dele.
   */
  const enlistPlacement = (p: MapPlacementData, idx: number, at: Vec2): void => {
    const className = p.className || (classes[0]?.name ?? 'Espadachim');
    const weaponName = p.weaponName || (weapons[0]?.name ?? 'Espada Curta');
    const team = teamOf(p);

    const look = defaultLook(team, idx);
    /*
     * O acervo entra ENTRE a anotação do mapa e o rosto genérico.
     *
     * O que o mapa declara é escolha do autor e vem primeiro; o que ele
     * deixou em branco para uma unidade que TEM nome de personagem — porque
     * o mapa foi anotado antes de o painel existir, ou porque o autor só
     * escreveu o nome — sai da ficha desse personagem, e não de um aldeão
     * qualquer.
     */
    const known = p.name
      ? (loaded.roster ?? []).find((c) => c.name === p.name)
      : undefined;
    const knownEnemy = team === 'inimigo'
      ? (p.battler
          ? (loaded.enemies ?? []).find((e) => e.battler === p.battler)
          : p.name
            ? (loaded.enemies ?? []).find((e) => e.name.toLowerCase() === p.name?.toLowerCase())
            : undefined)
      : undefined;
    const portrait = p.portrait || known?.portrait;
    const sprite = p.sprite || known?.mapSprite || knownEnemy?.mapSprite;
    const battler = p.battler || known?.battler || knownEnemy?.battler;
    const defaultPresentation = {
      portrait: portrait || look.portrait,
      sprite: sprite || look.sprite,
      battler: battler || look.battler,
    };

    // Unidade sem folha de caminhada e com battler é o monstro vindo do
    // editor de inimigos: ele vai ao tabuleiro com a própria cara. A folha de
    // reserva continua no lugar para o caso de o battler não estar no acervo.
    const battlerOnMap = !sprite && Boolean(defaultPresentation.battler);
    const battlerAsPortrait = !portrait && Boolean(defaultPresentation.battler);

    /*
     * A unidade anotada no mapa também herda a área declarada no bestiário.
     *
     * O editor de mapas planta o monstro pelo battler dele, e não copia a
     * ficha inteira; sem esta consulta a área escolhida no painel valeria só
     * nas escaramuças sorteadas, e o mesmo dragão ocuparia quatro casas num
     * mapa e uma no outro.
     */
    const bestiary = knownEnemy ?? (p.battler
      ? (loaded.enemies ?? []).find((e) => e.battler === p.battler)
      : undefined);
    const declared = bestiary?.size;
    // E a peçonha, pelo mesmo motivo: o gnomo plantado no mapa envenena como
    // o gnomo sorteado na escaramuça. Só do lado de lá — o monstro
    // recrutado pelo autor para o seu lado não traz o veneno de fábrica.
    const inflicts = team === 'inimigo' ? cleanAfflictions(bestiary?.inflicts) : undefined;

    const recruit: Recruit = {
      name: p.name || (team === 'jogador' ? `Herói ${idx + 1}` : `Inimigo ${idx + 1}`),
      className,
      weaponName,
      portrait: defaultPresentation.portrait,
      sprite: defaultPresentation.sprite,
      battler: defaultPresentation.battler,
      ...(declared ? { size: declared } : {}),
      ...(inflicts ? { inflicts } : {}),
      battlerOnMap,
      battlerAsPortrait,
      commander: Boolean(p.isCommander),
      aiProfile: toAiProfile(p.aiProfile),
      level: p.level ?? (team === 'inimigo' ? (options.enemyLevel ?? 1) : 1),
    };

    enlist(recruit, team, at);
  };

  if (rawPlacements.length > 0 && options.squad) {
    /*
     * O capítulo (e o desafio online) jogam as tropas DO MAPA.
     *
     * Este era o buraco do Modo História: um mapa com tropa anotada entregava
     * o tabuleiro inteiro ao desenho do autor — inclusive o lado do jogador —,
     * e um mapa SEM tropa anotada sorteava os dois lados no bestiário. Como os
     * capítulos apontavam para mapas do acervo, que não trazem anotação
     * nenhuma, o capítulo cuidadosamente montado no editor abria com um
     * punhado de monstros sorteados, em casas sorteadas, e nenhum dos inimigos
     * que o autor tinha posicionado.
     *
     * A divisão certa é por LADO, e não por mapa: o que o autor anotou do lado
     * de lá (inimigos e aliados) é o que entra em campo, e o lado do jogador é
     * o esquadrão que ele construiu — que é do que a campanha trata. As casas
     * que o mapa reserva ao jogador viram as casas de partida desse esquadrão.
     */
    const mapForces = rawPlacements.filter((p) => teamOf(p) !== 'jogador');
    const homeWishes = rawPlacements.filter((p) => teamOf(p) === 'jogador').map((p) => p.at);

    /*
     * Só quando o mapa não traz inimigo nenhum é que o bestiário volta a
     * povoar o lado de lá. Um capítulo anotado só com as casas de partida do
     * jogador ainda tem de ser uma batalha; sem isto ele abriria vencido.
     */
    const generatedFoes = mapForces.some((p) => teamOf(p) === 'inimigo')
      ? []
      : monsterSquad(loaded.enemies ?? [], seed, foeCount, options.enemyLevel);

    // Uma conferência só para todo mundo: duas unidades não podem querer a
    // mesma casa, e ninguém pode nascer fora do campo de batalha.
    const wanted: (Vec2 | undefined)[] = [
      ...mapForces.map((p) => p.at),
      ...playerSquad.map((_, i) => homeWishes[i] ?? playerSpots[i]),
      ...generatedFoes.map((_, i) => enemySpots[i]),
    ];
    const spots = settleOnBattlefield(map, wanted);
    const fallback: Vec2 = { x: 1, y: 1 };

    mapForces.forEach((p, i) => enlistPlacement(p, i, spots[i] ?? p.at ?? fallback));
    playerSquad.forEach((r, i) =>
      enlist(dressed(r), 'jogador', spots[mapForces.length + i] ?? fallback));
    generatedFoes.forEach((r, i) =>
      enlist(r, 'inimigo', spots[mapForces.length + playerSquad.length + i] ?? fallback));
  } else if (rawPlacements.length > 0) {
    // O mapa diz onde cada um começa; isto confere que dá para começar ali.
    const spots = settleOnBattlefield(map, rawPlacements.map((p) => p.at));

    rawPlacements.forEach((p, idx) => enlistPlacement(p, idx, spots[idx] ?? p.at ?? { x: 1, y: 1 }));
  } else {
    // O bestiário do acervo povoa o lado da CPU; a lista fixa fica de reserva
    // para quando o acervo não veio junto.
    const foes = monsterSquad(loaded.enemies ?? [], seed, foeCount, options.enemyLevel);
    playerSquad.forEach((r, i) => enlist(dressed(r), 'jogador', playerSpots[i] ?? { x: 1, y: 1 }));
    foes.forEach((r, i) => enlist(r, 'inimigo', enemySpots[i] ?? { x: 2, y: 1 }));
  }

  /*
   * Quem o capítulo deixa ser convencido a mudar de lado.
   *
   * Feito DEPOIS de todo mundo estar em campo, e não durante o alistamento,
   * por duas razões. A primeira é que o capítulo fala em nomes — "Clodz é
   * convencido por Distortidus" — e o motor precisa de ids, que só existem
   * quando as unidades existem. A segunda é que o esquadrão inimigo nem sempre
   * vem do mapa: os mapas do acervo não trazem tropa anotada, e a partida os
   * povoa com o bestiário. Casar pelo nome no fim atende aos dois casos com o
   * mesmo código.
   *
   * O primeiro homônimo leva o convite: um capítulo que diz "Goblin" num mapa
   * com cinco goblins recruta UM goblin, e não cinco.
   */
  const recruitBook = (options.recruits ?? []).filter((r) => r.unitName?.trim());
  if (recruitBook.length > 0) {
    const key = (name: string): string => name.trim().toLowerCase();
    const idOfName = new Map<string, number>();
    for (const u of units) {
      if (!idOfName.has(key(u.name))) idOfName.set(key(u.name), u.id);
    }

    const taken = new Set<number>();
    for (const entry of recruitBook) {
      const index = units.findIndex(
        (u) => u.team === 'inimigo' && !taken.has(u.id) && key(u.name) === key(entry.unitName),
      );
      if (index < 0) continue;
      const unit = units[index]!;
      taken.add(unit.id);

      /*
       * Um nome que não está em campo — o personagem não entrou neste
       * capítulo, ou o autor escreveu outro — deixa a conversa aberta a
       * qualquer um. O contrário (uma lista que ninguém satisfaz) seria um
       * recruta inalcançável, sem nada na tela explicando por quê.
       */
      const by = entry.byName?.trim() ? idOfName.get(key(entry.byName)) : undefined;
      units[index] = { ...unit, recruitable: by !== undefined ? { by: [by] } : {} };

      // O personagem do acervo é o que faz o recruta durar mais que a batalha:
      // é por ele que a ficha encontra o esquadrão guardado do jogador.
      if (entry.characterId) {
        const look = presentation.get(unit.id);
        if (look) presentation.set(unit.id, { ...look, characterId: entry.characterId });
      }
    }
  }

  const finalObjective = toObjective(options.objective ?? rawObjective);
  // Os corpos grandes só encontram lugar depois que todo mundo tem casa: até
  // aqui o plantio pensa em casas, uma por unidade.
  const settled = settleBodies(map, units);

  /*
   * Os baús entram no ESTADO, e não ficam no mapa.
   *
   * Porque eles mudam durante a partida: um baú aberto continua aberto, e o
   * replay precisa reencontrar em que turno isso aconteceu. Vem depois de todo
   * mundo estar plantado porque o baú de suprimento precisa saber que casas já
   * estão ocupadas.
   */
  const annotated = map.chests ?? [];
  /*
   * O mapa que traz os seus baús manda; o capítulo só abastece quem não traz.
   *
   * O desenho do autor vence a mão automática — plantar mais três baús num
   * mapa cujo autor escolheu esconder um só desfaria a escolha dele.
   */
  const planted = annotated.length > 0 || !options.supplies
    ? annotated
    : supplyChests(loaded, seed, supplyCount(map), {
        taken: settled.flatMap((u) => footprint(u.position, u.size)),
        // O que o SEU lado consegue empunhar: um arco no baú de um esquadrão
        // sem arqueiro é um desvio cobrado por nada.
        weaponTypes: new Set(
          settled
            .filter((u) => u.team === 'jogador')
            .flatMap((u) => classes.find((c) => c.id === u.classId)?.weaponTypes ?? []),
        ),
      });

  const chests = planted
    .filter((c) => {
      if (!c.at) return false;
      // Baú com prêmio que já não existe no acervo é descartado: abrir e não
      // receber nada é pior do que não ter baú.
      return c.itemId !== undefined
        ? (loaded.items ?? []).some((i) => i.id === c.itemId)
        : weapons.some((w) => w.id === c.weaponId);
    })
    .map((c, index) => ({
      id: index + 1,
      at: { x: c.at.x, y: c.at.y },
      ...(c.itemId !== undefined ? { itemId: c.itemId } : { weaponId: c.weaponId! }),
      ...(c.locked === false ? { locked: false } : {}),
    }));

  return {
    state: {
      formatVersion: 1,
      seed,
      rngState: seed,
      turn: 1,
      phase: 'jogador',
      map: toGameMap(map),
      units: settled,
      objective: finalObjective,
      status: 'em_andamento',
      // A fase de aliados só entra quando o mapa realmente traz um: acrescentá-la
      // sempre mudaria a contagem de turnos de todas as escaramuças existentes
      // (e, com ela, o resultado determinístico das que já foram gravadas).
      turnOrder: settled.some((u) => u.team === 'aliado')
        ? (['jogador', 'aliado', 'inimigo'] as const)
        : (['jogador', 'inimigo'] as const),
      // Ausente é "a progressão vale", que é o comportamento de sempre — e é
      // como todo estado gravado antes desta regra continua sendo lido.
      // `'jogador'` é a campanha: só o esquadrão do jogador aprende.
      ...(options.progression === false
        ? { progression: false as const }
        : options.progression === 'jogador'
          ? { progression: 'jogador' as const }
          : {}),
      // Idem para os baús: mapa sem baú não ganha a lista.
      ...(chests.length > 0 ? { chests } : {}),
    },
    presentation,
  };
}
